import { App, AppBlock, AppDatabaseUsage, AppStatusError, ContainerAppFeature, ContainerFeature, AuthenticationError, AuthorizationError, BlockedResourceError, InvalidInputError, NetworkError, NotFoundError, Plan, RepositoryBuildInfo, Subscription, User } from "@back4app2/sdk";
import { useEffect, useMemo, useReducer, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import back4app2 from "../back4app2";
import { hasReadPermission, hasWritePermission } from "../utils/permissions";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import TextInput from "../components/FormInputField/TextInput/TextInput";
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from "../settings";
import RuntimeVersionField, { isValidRuntimePin } from "../components/BuildMethodSelector/RuntimeVersionField";
import ToggleSwitchCheckbox from "../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox";
import toast from "react-hot-toast";
import { StorageUsageStrip } from "./database/StorageUsage";
import { ReactComponent as PlusIcon } from '../assets/images/plus-icon.svg';
import { ReactComponent as TrashIcon } from "../assets/images/trash-icon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/images/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../assets/images/visibility-off-icon.svg";
import { ReactComponent as AttentionIconSVG } from "../assets/images/circle-exclaimation-icon.svg";
import { ReactComponent as TriangleExclamationIcon } from '../assets/images/triangle-exclamation.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { ReactComponent as CopySVG } from '../assets/images/copy-icon.svg';
import Modal from "../components/Modal";
import CustomDomainSettings from "../components/CustomDomainSettings";
import BuildMethodSelector, { BuildMethod } from "../components/BuildMethodSelector/BuildMethodSelector";
import RepositoryBrowser, { ROOT_PATH } from "../components/RepositoryBrowser/RepositoryBrowser";
import { ReactComponent as FolderIcon } from '../assets/images/folder-icon.svg';
import ReactTooltip from "react-tooltip";
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { pushPaddlePurchaseEvent } from "../utils/gtm";
import { initializePaddle, InitializePaddleOptions } from '@paddle/paddle-js';
import Collaborators from "../components/Collaborators/Collaborators";
import GhostMessage from "../components/GhostMessage";
import { DsButton, Eyebrow, ModalDialog, SettingsSection, SurfaceCard, useSettingsSave } from '../components/ds';
import PlanPacksEditor, { PlanBillingMode } from './PlanPacksEditor';
import type { SettingsSaveState } from '../components/ds';
import { BoxIcon, CardIcon, CheckIcon, GlobeIcon, HeartbeatIcon, SettingsIcon, VariablesIcon } from '../components/ds/icons';
import { TOAST_SURFACE } from '../components/ds/toast';

export enum MyAppSettingsTab {
  GENERAL_SETTINGS = 'general-settings',
  BUILD_AND_DEPLOY = 'build-and-deploy',
  VARIABLES = 'variable',
  CUSTOM_DOMAIN = 'custom-domain',
  HEALTH_AND_ALERT = 'health-and-alert',
  PLAN = 'plan'
}

enum MyAppSettingsFieldName {
  BRANCH_NAME,
  ROOT_DIR,
  AUTO_DEPLOY,
  ENV_VARS,
  EXPOSED_PORT,
  HEALTH_CHECK_ENDPOINT,
  COLLABORATOR_EMAIL
}

interface MyAppSettingsState {
  currentTab: MyAppSettingsTab;
  isLoadingApp: boolean;
  appLoadingErrorMessage?: string;
  app?: App;
  isSubmitting: boolean;
  submittingErrorMessage?: string;
  isTriggerDeploymentModalOpen: boolean;
  plans?: Plan[];
  isLoadingPlans: boolean;
  loadingPlansErrorMessage?: string;
  // Paddle-related state
  isLoadingPaddle: boolean;
  paddleError?: string;
  paddle?: any;
  openCheckout: boolean;
  selectedPlan?: Plan;
}

const INITIAL_STATE: MyAppSettingsState = {
  currentTab: MyAppSettingsTab.GENERAL_SETTINGS,
  isLoadingApp: true,
  isSubmitting: false,
  isTriggerDeploymentModalOpen: false,
  isLoadingPlans: true,
  // Paddle-related initial state
  isLoadingPaddle: false,
  openCheckout: false,
};

enum MyAppSettingsActionType {
  RESET,
  FINISH_LOADING_APP,
  SET_CURRENT_TAB,
  SET_TRIGGER_DEPLOYMENT_MODAL,
  FINISH_UPDATING_APP,
  FINISH_LOADING_PLANS,
  // Paddle-related actions
  SET_PADDLE_LOADING,
  FINISH_LOADING_PADDLE,
  SET_CHECKOUT_MODAL,
  SET_SELECTED_PLAN,
}

const reset = () => ({
  type: MyAppSettingsActionType.RESET
} as const);

const setCurrentTab = (tab: MyAppSettingsTab) => ({
  type: MyAppSettingsActionType.SET_CURRENT_TAB,
  payload: tab
} as const);

const finishLoadingApp = (errorMessage?: string, app?: App) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const finishUpdatingApp = (app: App, errorMessage?: string) => ({
  type: MyAppSettingsActionType.FINISH_UPDATING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const setPaddleLoading = (isLoading: boolean) => ({
  type: MyAppSettingsActionType.SET_PADDLE_LOADING,
  payload: isLoading,
} as const);

const finishLoadingPlans = (errorMessage?: string, plans?: Plan[]) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PLANS,
  payload: {
    errorMessage,
    plans
  }
} as const);

const openTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: true,
} as const);

const closeTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: false,
} as const);

// Paddle-related action creators
const finishLoadingPaddle = (errorMessage?: string, paddle?: any) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PADDLE,
  payload: {
    errorMessage,
    paddle,
  },
} as const);

const setCheckoutModal = (isOpen: boolean) => ({
  type: MyAppSettingsActionType.SET_CHECKOUT_MODAL,
  payload: isOpen,
} as const);

const setSelectedPlan = (plan?: Plan) => ({
  type: MyAppSettingsActionType.SET_SELECTED_PLAN,
  payload: plan,
} as const);

type MyAppSettingsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingApp> | ReturnType<typeof setCurrentTab> | ReturnType<typeof openTriggerDeploymentModal> | ReturnType<typeof closeTriggerDeploymentModal> | ReturnType<typeof finishUpdatingApp> | ReturnType<typeof finishLoadingPlans> | ReturnType<typeof setPaddleLoading> | ReturnType<typeof finishLoadingPaddle> | ReturnType<typeof setCheckoutModal> | ReturnType<typeof setSelectedPlan>;

const reducer = (state: MyAppSettingsState = INITIAL_STATE, action: MyAppSettingsAction): MyAppSettingsState => {
  switch (action.type) {
    case MyAppSettingsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppSettingsActionType.FINISH_LOADING_APP:
      return {
        ...state,
        isLoadingApp: false,
        appLoadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app
      }
    case MyAppSettingsActionType.SET_CURRENT_TAB: {
      return {
        ...state,
        currentTab: action.payload || MyAppSettingsTab.GENERAL_SETTINGS
      }
    }
    case MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL:
      return {
        ...state,
        isTriggerDeploymentModalOpen: action.payload
      }
    case MyAppSettingsActionType.FINISH_UPDATING_APP:
      return {
        ...state,
        app: action.payload.app,
        isSubmitting: false,
        submittingErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.FINISH_LOADING_PLANS:
      return {
        ...state,
        plans: action.payload.plans,
        isLoadingPlans: false,
        loadingPlansErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.SET_PADDLE_LOADING:
      return {
        ...state,
        isLoadingPaddle: action.payload
      }
    case MyAppSettingsActionType.FINISH_LOADING_PADDLE:
      return {
        ...state,
        paddle: action.payload.paddle,
        paddleError: action.payload.errorMessage,
        isLoadingPaddle: false,
      }
    case MyAppSettingsActionType.SET_CHECKOUT_MODAL:
      return {
        ...state,
        openCheckout: action.payload
      }
    case MyAppSettingsActionType.SET_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: action.payload
      }
  }
};

type AppForm = {
  branchName?: string, 
  rootDir?: string, 
  envs?: { key: string; value?: string }[], 
  autoDeploy?: boolean,
  exposedPort?: number,
  healthCheckEndpoint?: string,
  startCommand?: string,
  builderPreference?: string,
  runtimeVersion?: string,
  // Name the managed database's connection string goes out under.
  databaseUrlEnvKey?: string,
  // The app's own connection string in its place; '' goes back to Back4app's.
  databaseUrlValue?: string
}

const ENV_VARIABLE_MAX_LENGTH = 1023;

/** Mirrors `DEFAULT_DATABASE_URL_ENV_KEY` in controllers. */
const DEFAULT_DATABASE_URL_ENV_KEY = 'DATABASE_URL';

/**
 * Where "Upgrade" lands.
 *
 * It used to be `?section=plan#general-settings`: the hash carried the tab, so
 * the section to scroll to could not also live there. Plan is its own section
 * now, so the hash says the whole thing and nothing has to scroll on arrival.
 * Every Upgrade in the product still goes through here.
 */
export const upgradePath = (appId: string) =>
  `/apps/${appId}/settings#${MyAppSettingsTab.PLAN}`;

/**
 * The sections of Settings, and everything anyone needs to name one.
 *
 * Hoisted out of the screen because the shell draws them too: on a phone the
 * list is a menu hanging off the Settings tab, which has to be able to open
 * over Overview or Logs — screens where MyAppSettings is not mounted at all.
 */
export interface SettingsTab {
  id: MyAppSettingsTab;
  label: string;
  title: string;
  blurb: string;
  /** The sidebar draws these; the mobile picker does not. */
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  count?: number;
  danger?: boolean;
  ownerOnly?: boolean;
}

export const getSettingsTabs = (app?: App): SettingsTab[] => [
    {
      id: MyAppSettingsTab.GENERAL_SETTINGS,
      label: 'General',
      title: 'General',
      // Deleting the app lives here now. It was a section of its own called
      // Danger zone — a whole entry in the menu, and the fifth of six, whose
      // one row was a Delete button. A menu entry is a place you go; nobody
      // goes to the danger zone. It reads better as the last thing on the
      // screen that already holds the app's name and its people.
      blurb: 'Name, who else can work on this app, and deleting it.',
      Icon: SettingsIcon,
    },
    {
      id: MyAppSettingsTab.BUILD_AND_DEPLOY,
      label: 'Build & deploy',
      title: 'Build & deploy',
      blurb: 'Where the code comes from, how it is built, and when it deploys.',
      Icon: BoxIcon,
    },
    {
      id: MyAppSettingsTab.VARIABLES,
      label: 'Environment',
      title: 'Environment variables',
      blurb: 'Available at build time and at runtime. A change needs a redeploy.',
      count: app?.mainService?.mainServiceEnvironment?.envVars?.length,
      Icon: VariablesIcon,
    },
    {
      id: MyAppSettingsTab.CUSTOM_DOMAIN,
      label: 'Domains',
      title: 'Domains',
      blurb: 'Where your app answers. SSL is issued automatically.',
      Icon: GlobeIcon,
    },
    {
      id: MyAppSettingsTab.HEALTH_AND_ALERT,
      label: 'Health & alerts',
      title: 'Health & alerts',
      blurb: 'The path Back4app checks before releasing a deploy.',
      Icon: HeartbeatIcon,
    },
    {
      // Its own section, not a block at the bottom of General. It is the one
      // subject here that is a table of six options and a purchase, and under
      // General it sat below Collaborators — so every Upgrade in the product
      // had to carry a `?section=` to scroll past two other subjects to reach
      // it.
      id: MyAppSettingsTab.PLAN,
      label: 'Plan',
      title: 'Plan',
      blurb: 'The CPU, RAM and transfer this app runs with. Changes are billed pro rata, and a plan change redeploys the app.',
      Icon: CardIcon,
    },
];

export const getVisibleSettingsTabs = (app?: App): SettingsTab[] =>
  getSettingsTabs(app).filter(tab => !tab.ownerOnly || (app && app.isOwner));

const MyAppSettings = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateIsDeletingApp, user, saveState } = useOutletContext<{
    updateIsDeletingApp: (isDeleting: boolean) => void,
    user: User,
    /** Owned by the shell, which draws the bar and the section menu together. */
    saveState: SettingsSaveState | null,
    goToSettingsTab: (id: string) => void,
  }>();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [isFreePlan, setisFreePlan] = useState(true); // Default to true to be safe
  const { currentTab, app, isLoadingApp, appLoadingErrorMessage, isSubmitting, isTriggerDeploymentModalOpen, plans, isLoadingPlans, loadingPlansErrorMessage, paddle, openCheckout } = state;
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

  useEffect(() => {
    dispatch(setCurrentTab(location.hash.replace('#', '') as MyAppSettingsTab));
  }, [location.hash]);

  /**
   * `?section=` scrolls, once the tab it belongs to has rendered.
   *
   * The tab lives in the hash, so the section cannot; and a plain anchor would
   * not work anyway, because the scroller is `main` up in MyApp rather than the
   * document. `isLoadingApp` is in the deps because the section does not exist
   * until the app does.
   */
  const sectionParam = new URLSearchParams(location.search).get('section');
  useEffect(() => {
    if (!sectionParam || isLoadingApp) {
      return;
    }
    // One frame, so the tab's content is mounted before we look for it.
    const raf = requestAnimationFrame(() => {
      document.getElementById(sectionParam)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [sectionParam, currentTab, isLoadingApp]);

  useEffect(() => {
    let appSubscription: Subscription;
    (async() => {

      if (appId) {
        try {
          const app: App = await back4app2.findAppById(appId);
          dispatch(finishLoadingApp(undefined, app));
        } catch (err) {
          let errMsg = '';
          if (err instanceof NetworkError) {
            console.error('network error', err);
            errMsg = 'Network error when fetching app settings. Check your internet connection and try again.';
          } else if (err instanceof NotFoundError) {
            errMsg = 'App not found!'
          } else {
            console.error('unexpected error fetching app settings', err);
            errMsg = 'Unexpected error while fetching app settings. Please try again.'
          }
          dispatch(finishLoadingApp(errMsg, undefined));
        }

        appSubscription = back4app2.subscribeToApp(appId, (error, snapshot) => {
          if (error) {
            setisFreePlan(true);
          } else if (snapshot) {
            // Check if the app is on a free plan
            const mainServiceEnvironment = snapshot.mainService && snapshot.mainService.mainServiceEnvironment;
            const isPendingPayment = mainServiceEnvironment && (
              mainServiceEnvironment.isPendingPayment || 
              (!mainServiceEnvironment.isFreePlanElegible && mainServiceEnvironment.plan.name.includes('Free'))
            );
            const isFree = mainServiceEnvironment?.plan.name.includes('Free') || false;
            setisFreePlan(isFree || !!isPendingPayment);
          }
        });
      }
    })();

    return () => {
      if (appSubscription) {
        appSubscription.unsubscribe();
      }
    }
  }, [appId]);

  useEffect(() => {
    (async() => {
      try {
        const plans = await back4app2.getPlans();
        dispatch(finishLoadingPlans(undefined, plans));
      } catch (err) {
        let errMsg = '';
        if (err instanceof NetworkError) {
          console.error('network error', err);
          errMsg = 'Network error when fetching plans. Check your internet connection and try again.';
        } else {
          console.error('unexpected error fetching plans', err);
          errMsg = 'Unexpected error while fetching plans. Please try again.'
        }
        dispatch(finishLoadingPlans(errMsg));
      }
    })();
  }, []);

  const loadPaddle = useCallback(() => {
    const paddleToken = process.env.REACT_APP_PADDLE_TOKEN;

    if (!paddleToken) {
      // Sem fallback, de propósito: um token de outra conta inicializa o Paddle
      // com sucesso e só falha lá na frente, no checkout, na frente do comprador.
      console.error('REACT_APP_PADDLE_TOKEN is not set; checkout is unavailable');
      dispatch(finishLoadingPaddle('Paddle is not configured', undefined));

      return;
    }

    const paddleOptions = {
      token: paddleToken,
      environment: process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox',
      pwCustomer: {}
    };

    const paddleEventCallback = async function (data: any) {
      if (data.name === 'checkout.completed') {
        pushPaddlePurchaseEvent(data.data);

        const paymentData = {
          appId: data.data.custom_data.app_id,
          customerId: data.data.customer.id,
          planName: data.data.items[0].product.name,
          transactionId: data.data.transaction_id,
          checkoutId: data.data.id,
          results: data.data,
          planId: data.data.custom_data.plan_id,
          email: data.data.customer.email,
        };

        // Save subscription
        try {
          await fetch(`${process.env.REACT_APP_BACK4APP_CHECKOUT_URL}/save-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          });
        } catch (error) {
          console.error('Error saving subscription:', error);
          toast.error('Error updating plan information, please contact support!');
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));

        toast.success('Plan updated successfully!', {
          className: TOAST_SURFACE,
          icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });

        await new Promise(resolve => setTimeout(resolve, 1000));

        navigate(0);
      }
      if (data.name === 'checkout.closed') {
        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));
      }
    };

    initializePaddle({ ...paddleOptions, eventCallback: paddleEventCallback } as InitializePaddleOptions).then(
      (paddleInstance: any) => {
        if (paddleInstance) {
          dispatch(finishLoadingPaddle(undefined, paddleInstance));
        }
      },
    ).catch((error) => {
      console.error('Paddle initialization failed:', error);
      dispatch(finishLoadingPaddle('Failed to initialize Paddle', undefined));
      toast.error('Failed to open checkout, please contact support!', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    });
  }, [navigate]);

  // Paddle initialization
  useEffect(() => {
    loadPaddle();
  }, [loadPaddle]);

  const handleOnClickPlan = async (plan: Plan) => {
    if (plan.name === 'Free') {
      toast.error('Please contact support for Free plan!', {
        className: TOAST_SURFACE,
        icon: <AttentionIconSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }
    dispatch(setSelectedPlan(plan));
    dispatch(setCheckoutModal(true));

    // Wait for the modal to be rendered before opening Paddle checkout
    setTimeout(async () => {
      if (paddle) {
        const paddlePriceId = process.env.REACT_APP_SENTRY_ENV === 'production' ? plan.checkoutPriceKey : 'pri_01jj29jwpxef927zy0waqcdtjh';

        let userEmail = user.username;

        if (app && !app.isOwner) {
          userEmail = await back4app2.getAppOwnerEmail(app.id);
        }
        
        try {
          paddle.Checkout.open({
            items: [{ priceId: paddlePriceId, quantity: 1 }],
            title: plan.name,
            settings: {
              displayMode: 'inline',
              theme: 'light',
              locale: 'en',
              variant: 'one-page',
              frameTarget: 'checkout-container',
              frameInitialHeight: '450',
              frameStyle: 'width: 100%; min-width: 312px; max-height: 80vh; background-color: #f9f9f9; border: none;'
            },
            customData: { appId: appId, planId: plan.checkoutPlanId },
            allowLogout: false,
            customer: {
              email: userEmail,
            }
          });
        } catch (error) {
          console.error('Error opening Paddle checkout inline:', error);
          try {
            paddle.Checkout.open({
              items: [{ priceId: paddlePriceId, quantity: 1 }],
              title: plan.name,
              customData: { appId: appId, planId: plan.checkoutPlanId },
              allowLogout: false,
              customer: {
                email: user.username,
              }
            });
          } catch (fallbackError) {
            console.error('Fallback Paddle checkout also failed:', fallbackError);
          }
        }
      } else {
        console.error('Paddle instance not available');
      }
    }, 200); // Increased delay to ensure modal is fully rendered
  };

  const handleSubmit = async (newValues: AppForm) => {
    // check if something change
    let isSettingsUpdated = false;
    if (newValues.branchName && newValues.branchName !== app?.mainService?.mainServiceEnvironment?.branchName) {
      isSettingsUpdated = true;
    }
    if (newValues.rootDir && newValues.rootDir !== app?.mainService?.mainServiceEnvironment?.rootDirPath) {
      isSettingsUpdated = true;
    }
    if (typeof newValues.autoDeploy !== "undefined" && newValues.autoDeploy !== app?.mainService?.mainServiceEnvironment?.autoDeploy) {
      isSettingsUpdated = true;
    }
    if (newValues.envs) {
      if (app?.mainService?.mainServiceEnvironment?.envVars.length !== newValues.envs.length) {
        isSettingsUpdated = true;
      } else {
        // check each envs
        for (let idx = 0; idx < newValues.envs.length; idx++) {
          const newEnv = newValues.envs[idx];
          const sameEnv = app.mainService.mainServiceEnvironment.envVars.find(oldEnv => {
            if (oldEnv.key === newEnv.key && oldEnv.value === newEnv.value) return false;
            else if (oldEnv.key === newEnv.key && oldEnv.value !== newEnv.value) return true;
            else if (oldEnv.key !== newEnv.key && oldEnv.value === newEnv.value) return true;
            return false;
          });
          if (sameEnv) {
            isSettingsUpdated = true; break;
          }
        }
      }
    }
    if (newValues.exposedPort !== app?.mainService?.mainServiceEnvironment?.exposedPort) {
      isSettingsUpdated = true;
    }
    if (newValues.startCommand !== undefined && newValues.startCommand !== (app?.mainService?.mainServiceEnvironment?.startCommand || '')) {
      isSettingsUpdated = true;
    }
    // NULL reads as DOCKERFILE, so an app that never had the setting is only
    // "updated" when the user actually moves off Dockerfile.
    if (newValues.builderPreference !== undefined && newValues.builderPreference !== (app?.mainService?.mainServiceEnvironment?.builderPreference || 'DOCKERFILE')) {
      isSettingsUpdated = true;
    }
    if (newValues.runtimeVersion !== undefined && newValues.runtimeVersion !== (app?.mainService?.mainServiceEnvironment?.runtimeVersion || '')) {
      isSettingsUpdated = true;
    }
    if (newValues.healthCheckEndpoint && newValues.healthCheckEndpoint !== app?.mainService?.mainServiceEnvironment?.healthCheckEndpoint) {
      isSettingsUpdated = true;
    }
    if (newValues.databaseUrlEnvKey !== undefined && newValues.databaseUrlEnvKey !== (app?.mainService?.mainServiceEnvironment?.databaseUrlEnvKey || DEFAULT_DATABASE_URL_ENV_KEY)) {
      isSettingsUpdated = true;
    }
    if (newValues.databaseUrlValue !== undefined) {
      isSettingsUpdated = true;
    }

    if (!isSettingsUpdated) {
      toast('Please make some changes to save new settings!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-md text-sm",
        icon: <AttentionIconSVG width="20px" height="20px" className="animate-bounce-in text-old-blue" />
      });
      return false;
    }

    const toastId = toast.loading('Updating settings...', {
      className: TOAST_SURFACE,
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const updatedApp = await back4app2.updateAppSettings(appId as string, newValues.branchName, newValues.rootDir, newValues.autoDeploy, newValues.envs, newValues.healthCheckEndpoint, newValues.exposedPort, newValues.startCommand, newValues.builderPreference, newValues.runtimeVersion, newValues.databaseUrlEnvKey, newValues.databaseUrlValue);
      dispatch(finishUpdatingApp(updatedApp, undefined));
      toast.success('Settings were updated!', {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      // Only show deployment modal if user has write permission for Deploy
      if (updatedApp && hasWritePermission(updatedApp, 'Deploy')) {
        dispatch(openTriggerDeploymentModal());
      }
      return true;
    } catch (err) {
      let errMsg = 'Something went wrong!';
      // InvalidInputError carries a message written for this exact field (a
      // traversing Root Directory, a broken Start Command). Falling through to
      // the generic toast throws away the only thing that tells the user what
      // to change.
      if (err instanceof AppStatusError || err instanceof AuthorizationError || err instanceof InvalidInputError) {
        errMsg = err.message;
      } else {
        console.error('error', err);
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
      return false;
    }
  }

  const getFormContent = () => {
    let formContent;
    if (!app) {
      return;
    }
    switch (currentTab) {
      case MyAppSettingsTab.GENERAL_SETTINGS: {
        if (!hasReadPermission(app, 'General')) {
          return null;
        }
        formContent = <GeneralSettingsForm
          currentUser={user}
          appId={app.id}
          appName={app.name}
          isOwner={app.isOwner}
          block={app.block}
          updateIsDeletingApp={updateIsDeletingApp}
        />;
        break;
      }
      case MyAppSettingsTab.BUILD_AND_DEPLOY: {
        formContent = <BuildAndDeployForm
          sourceKind={app.mainService?.mainServiceEnvironment?.sourceKind || 'GITHUB'}
          imageRepository={app.mainService?.mainServiceEnvironment?.imageRepository || ''}
          imageTag={app.mainService?.mainServiceEnvironment?.imageTag || ''}
          branchName={app.mainService?.mainServiceEnvironment?.branchName || ''}
          rootDir={app.mainService?.mainServiceEnvironment?.rootDirPath || ''}
          repositoryId={app.mainService?.repository?.id || ''}
          autoDeploy={!!app.mainService?.mainServiceEnvironment?.autoDeploy}
          exposedPort={`${app.mainService?.mainServiceEnvironment?.exposedPort || ''}`}
          startCommand={app.mainService?.mainServiceEnvironment?.startCommand || ''}
          isStaticSite={!!app.mainService?.mainServiceEnvironment?.isStaticSite}
          builderPreference={app.mainService?.mainServiceEnvironment?.builderPreference || ''}
          runtimeVersion={app.mainService?.mainServiceEnvironment?.runtimeVersion || ''}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          plan={app.mainService?.mainServiceEnvironment?.plan}
          isFreePlan={isFreePlan}
          canWrite={hasWritePermission(app, 'BuildAndDeploy')}
          canRead={hasReadPermission(app, 'BuildAndDeploy')}
        />;
        break;
      }
      case MyAppSettingsTab.VARIABLES: {
        formContent = <VariablesForm appId={app.id} envVars={app.mainService?.mainServiceEnvironment?.envVars || []} databaseUrlEnvKey={app.mainService?.mainServiceEnvironment?.databaseUrlEnvKey || DEFAULT_DATABASE_URL_ENV_KEY} hasDatabaseUrlOverride={!!app.mainService?.mainServiceEnvironment?.hasDatabaseUrlOverride} isSubmitting={isSubmitting} handleSubmit={handleSubmit} canWrite={hasWritePermission(app, 'EnvVars')} canRead={hasReadPermission(app, 'EnvVars')} />;
        break;
      }
      case MyAppSettingsTab.CUSTOM_DOMAIN: {
        formContent = <CustomDomainSettings 
          appId={app.id}
          serviceEnvironmentId={mainServiceEnvironmentId as string} 
          mainCustomDomainId={app.mainService?.mainServiceEnvironment?.mainCustomDomain?.id as string}
          onClickUpgrade={() => navigate(upgradePath(appId as string))}
          canRead={hasReadPermission(app, 'Domain')}
          canWrite={hasWritePermission(app, 'Domain')}
          canDeploy={hasWritePermission(app, 'Deploy')}
        />
        break;
      }
      case MyAppSettingsTab.HEALTH_AND_ALERT: {
        formContent = <HealthAndAlertForm healthCheckEndpoint={app.mainService?.mainServiceEnvironment?.healthCheckEndpoint || ''}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          canWrite={hasWritePermission(app, 'HealthAndAlert')}
          canRead={hasReadPermission(app, 'HealthAndAlert')}
        />
        break;
      }
      case MyAppSettingsTab.PLAN: {
        formContent = <PlanForm
          appId={appId}
          isOwner={Boolean(app.isOwner)}
          plan={app.mainService?.mainServiceEnvironment?.plan}
          planCancelsAt={app.mainService?.mainServiceEnvironment?.planCancelsAt}
          hasPaddleSubscription={Boolean(app.mainService?.mainServiceEnvironment?.hasPaddleSubscription)}
          paymentPastDueAt={app.mainService?.mainServiceEnvironment?.paymentPastDueAt}
          isPendingPayment={Boolean(app.mainService?.mainServiceEnvironment?.isPendingPayment)}
          user={user}
          plans={plans}
          isLoadingPlans={isLoadingPlans}
          loadingPlansErrorMessage={loadingPlansErrorMessage}
          onPlanClick={handleOnClickPlan}
          onPlanCanceled={() => window.location.reload()}
        />;
        break;
      }
      default:
        break;
    }
    if (!app.mainService?.mainServiceEnvironment?.id && currentTab !== MyAppSettingsTab.GENERAL_SETTINGS) {
      formContent = (
        <div className="rounded-card border border-navy-500 bg-navy-800 px-4 py-10 text-center text-ds-135 text-navy-300">
          Nothing here, yet.
        </div>
      );
    }
    return formContent;
  }

  const handleTriggerDeployment = async () => {
    if (!app) {
      toast.error('App information is not available.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
    const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

    try {
      await back4app2.triggerManualDeployment(mainServiceEnvironmentId as string);
      dispatch(closeTriggerDeploymentModal());
      navigate(`/apps/${appId}`);
    } catch (err) {
      let errMsg = 'Something went wrong while triggering new deployment!';
      if (err instanceof NetworkError) {
        errMsg = 'Network error when triggering deployment. Check your internet connection and try again.';
      } else if (err instanceof AuthenticationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return;
      } else if (err instanceof AppStatusError || err instanceof NotFoundError || err instanceof AuthorizationError) {
        errMsg = err.message;
      } else {
        console.error('error triggering deployment', err);
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }

  // One place naming each section, so the head, the tab and the browser hash
  // cannot drift apart.
  const TABS = getSettingsTabs(app);

  const visibleTabs = TABS.filter(tab => !tab.ownerOnly || (app && app.isOwner));

  /**
   * Switching sections unmounts the form, and an unmounted form's edits are
   * gone. It used to happen in silence; now it asks.
   */
  const activeTab = visibleTabs.find(tab => tab.id === currentTab) || visibleTabs[0];

  const idPill = (
    <div className="inline-flex items-center gap-2 h-[30px] px-3 rounded-control border border-navy-500 bg-navy-700 font-courrier text-ds-115 text-navy-200">
      <span className="uppercase tracking-[0.12em] text-navy-300">id</span>
      <span className="text-navy-100 truncate max-w-[16ch]" title={appId}>{appId}</span>
      <button
        type="button"
        aria-label="Copy deployment ID"
        className="b4-focus rounded-[3px] text-navy-300 hover:text-white"
        data-tip="Copied!"
        data-event="click focus"
        onClick={() => navigator.clipboard.writeText(appId as string)}
      >
        <CopySVG className="flex-none" />
      </button>
    </div>
  );

  return (
    <>
      {/* The tab row sits on its own lighter ground, bleeding to the edges of
          the page. That change of surface is what separates "this names the
          section" from "this is the section" — the hairline alone was too
          quiet to read as a division. `-mx` cancels `main`'s padding exactly,
          so the text still lines up with everything else. */}
      <div className="-mx-4 lg:-mx-8 flex flex-col">
        {/* The ID sat beside the title and, on a phone, literally on top of
            it. It is metadata you copy once, so it drops under the head. */}
        <div className="px-4 lg:px-8 pb-5 border-b border-navy-700 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className="flex flex-col gap-2 min-w-0">
            <Eyebrow>settings</Eyebrow>
            <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">
              {activeTab.title}
            </h1>
            <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">{activeTab.blurb}</p>
          </div>
          <div className="flex-none">{idPill}</div>
        </div>

        {/* The strip of pills that used to sit here is in the sidebar now,
            under a SETTINGS rule. It was a second menu inside a screen the
            first menu had already taken you to, and it could only say where
            you could go — the sidebar says that AND stays put while you read
            the section, so leaving one setting for another is one click from
            anywhere rather than two from here.

            The phone keeps its picker: there is no sidebar there, and the
            picker is a sheet that names where you are, which is the one thing
            a strip this width could never do. */}
      </div>

      <div>
        {isLoadingApp ? <div className="flex h-full justify-center items-center"> <LoadingSpinner color='text-white' /> </div> : null}
        {appLoadingErrorMessage ? (<>
          <div className="flex h-full">
            <ErrorMessage message={appLoadingErrorMessage} />
          </div>
        </>) : (
          getFormContent()
        )}
      </div>

      {/* Reserves the height the fixed footer covers, so the last field of a
          section is never behind it. */}
      <div
        aria-hidden="true"
        className={saveState && (saveState.dirtyCount > 0 || saveState.alwaysVisible)
          ? saveState.total ? 'flex-none h-[84px]' : 'flex-none h-[62px] short:h-[52px]'
          : 'flex-none h-0'}
      />

      <ModalDialog
        open={isTriggerDeploymentModalOpen}
        onClose={() => dispatch(closeTriggerDeploymentModal())}
        title="Settings saved"
        blurb="They take effect on the next deploy. Deploy now with the settings you just saved?"
        footer={<>
          <DsButton onClick={() => dispatch(closeTriggerDeploymentModal())}>Not now</DsButton>
          <DsButton
            variant="primary"
            onClick={handleTriggerDeployment}
            isDisabled={!app || !hasWritePermission(app, 'Deploy')}
          >
            Deploy now
          </DsButton>
        </>}
      />
      <ReactTooltip
        className="tooltip shadow-[0px_0px_30px_rgba(0,0,0,0.4)]" 
        delayHide={2000} 
        place="top" 
        effect="solid" 
        html={true} 
        arrowColor="#303338" 
        eventOff="click" 
        afterShow={() => navigator.clipboard.writeText(appId as string)}
      />
      
      {/* Paddle Checkout Modal */}
      {openCheckout && (
        <Modal
          open={openCheckout}
          onClose={() => dispatch(setCheckoutModal(false))}
        >
          <div className="w-full max-w-4xl mx-auto">
            <div 
              id="checkout-container" 
              className="checkout-container w-full min-h-[450px] bg-white rounded-lg"
              style={{ minHeight: '450px' }}
            ></div>
          </div>
        </Modal>
      )}
    </>
  )
};

const GeneralSettingsForm = (props: { currentUser: User, appId: string; appName: string, isOwner: boolean, block?: AppBlock, updateIsDeletingApp: (isDeleting: boolean) => void }) => {
  const { currentUser, appId, appName, isOwner, block, updateIsDeletingApp } = props;

  return <div className="flex flex-col">
    <SettingsSection eyebrow="identity">
      {/* Read-only, and it always has been: there is no rename mutation in the
          API. The field is here because this is where you come to check the
          name, not to change it. */}
      <div className="max-w-[420px]">
        <TextInput
          value={appName}
          onChange={() => {}}
          label="Name"
          isDisabled={true}
          helpText="Shown across the dashboard."
        />
      </div>
    </SettingsSection>

    <Collaborators currentUser={currentUser} appId={appId} isOwner={isOwner} />

    {/* Last, and only for the owner — it was an owner-only section before, and
        folding it into General must not hand the button to a collaborator.
        It keeps an eyebrow of its own: the screen's title says "General" now,
        so the red is the only thing left saying which rows these are. */}
    {isOwner ? (
      <SettingsSection eyebrow={<span className="text-state-err">danger zone</span>}>
        <SuspendDeleteForm
          appName={appName}
          appId={appId}
          block={block}
          updateIsDeletingApp={updateIsDeletingApp}
        />
      </SettingsSection>
    ) : null}
  </div>
}

const PlanForm = (props: { appId?: string, isOwner?: boolean, plan?: Plan, planCancelsAt?: Date, hasPaddleSubscription?: boolean, paymentPastDueAt?: Date, isPendingPayment?: boolean, user?: User, plans?: Plan[], isLoadingPlans: boolean, loadingPlansErrorMessage?: string, onPlanClick?: (plan: Plan) => void, onPlanCanceled?: () => void }) => {
  const { appId, isOwner = true, plan: currentPlan, planCancelsAt, hasPaddleSubscription, paymentPastDueAt, isPendingPayment, user, isLoadingPlans, loadingPlansErrorMessage, plans, onPlanCanceled } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetched here rather than lifted into the screen's reducer: the extras are
  // only ever read by this section, and the screen already carries enough.
  const [features, setFeatures] = useState<ContainerAppFeature[] | undefined>(undefined);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  // Read from Paddle, and only when the dialog opens: it is one API call, and
  // nobody visiting this screen to change plans needs it.
  const [cycleEnd, setCycleEnd] = useState<Date | undefined>(undefined);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState('');
  const [isResuming, setIsResuming] = useState(false);
  const [resumeErrorMessage, setResumeErrorMessage] = useState('');
  // How much of the database extra is in use. Its own request, and a cheap
  // one: it reads our own rows only, so it never opens a connection to the
  // app's database and answers the same for an app that is stopped.
  const [usage, setUsage] = useState<AppDatabaseUsage | undefined>(undefined);

  useEffect(() => {
    if (!appId) {
      return;
    }

    let canceled = false;

    (async () => {
      try {
        const rows = await back4app2.containerAppFeatures(appId);

        if (!canceled) {
          setFeatures(rows);
        }
      } catch (e) {
        // The plan table is the subject here; a failed extras read must not
        // take the section down with it.
        console.error('could not load the app extras', e);

        if (!canceled) {
          setFeatures([]);
        }
      }

      try {
        const result = await back4app2.appDatabaseUsage(appId, 30);

        if (!canceled) {
          setUsage(result);
        }
      } catch (e) {
        console.error('could not load the database usage', e);
      }
    })();

    return () => { canceled = true; };
  }, [appId]);

  const isPaid = Boolean(currentPlan && currentPlan.price > 0);
  // A cancelled plan is not a former plan: it runs, and it was paid for, until
  // the date. Everything below reads this instead of hiding the plan away.
  const cancelsOn = planCancelsAt ? new Date(planCancelsAt) : undefined;
  const isScheduledToCancel = Boolean(cancelsOn && cancelsOn.getTime() > Date.now());
  const cancelsOnLabel = cancelsOn
    ? cancelsOn.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const cancelsOnShort = cancelsOn
    ? cancelsOn.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  const extrasTotal = (features || []).reduce((sum, feature) => sum + feature.price * feature.quantity, 0);
  const monthlyTotal = (currentPlan?.price || 0) + extrasTotal;

  /**
   * Which way this app changes plan.
   *
   * A paid app with no subscription of its own was paid through the legacy
   * checkout. The server looks for that subscription when the screen opens and
   * adopts it when it safely can (SUBSCRIPTION: changed in place). When it
   * cannot, the change is a new checkout at the full price and the team cancels
   * the old one (CHECKOUT). Neither answer blocks paying, and a failed lookup
   * is a checkout. Until it answers, the table only shows the current plan.
   */
  const needsBillingPath = isPaid && !hasPaddleSubscription;
  const [billingPath, setBillingPath] = useState<PlanBillingMode | undefined>(undefined);

  useEffect(() => {
    if (!appId || !needsBillingPath || !isOwner) {
      return;
    }

    let canceled = false;

    back4app2.resolveContainerBillingPath(appId)
      .then((path) => { if (!canceled) setBillingPath(path); })
      .catch((e) => {
        console.error('could not resolve how this app is billed', e);
        if (!canceled) setBillingPath('CHECKOUT');
      });

    return () => { canceled = true; };
  }, [appId, needsBillingPath, isOwner]);

  const isLegacyBilling = needsBillingPath && !billingPath;
  const replacesPreviousPlan = needsBillingPath && billingPath === 'CHECKOUT';
  const billingMode: PlanBillingMode = !isPaid ? 'CHECKOUT' : replacesPreviousPlan ? 'CHECKOUT' : 'SUBSCRIPTION';
  const [draftPlanId, setDraftPlanId] = useState<string | undefined>(isPaid ? currentPlan?.id : undefined);

  /**
   * `?add=<kind>` opens this screen with an extra already chosen — the Database
   * section's offer sends `database`, so the reader lands on the plan that
   * sells it with the extra in the basket and only has to pay.
   *
   * The KIND, not two ids: the ids are a pair of UUIDs that made the link
   * unreadable and pinned a catalogue decision into a URL people can bookmark.
   * What they meant is "I want a database" — so that is what travels, and the
   * ids are looked up here, where the catalogue already is. A kind nobody
   * sells resolves to nothing and the screen opens as it always did.
   */
  const wantedKind = searchParams.get('add') || undefined;
  const [wantedFeature, setWantedFeature] = useState<ContainerFeature | undefined>();
  /** Seeded once — after that the table is the reader's. */
  const hasSeededPlan = useRef(false);

  useEffect(() => {
    if (!wantedKind) {
      return;
    }

    let canceled = false;

    back4app2
      .containerFeatures()
      .then((rows) => {
        if (!canceled) {
          setWantedFeature(rows.find((row) => row.kind === wantedKind));
        }
      })
      .catch((e) => console.error('could not resolve the extra to add', e));

    return () => { canceled = true; };
  }, [wantedKind]);

  useEffect(() => {
    if (hasSeededPlan.current || !wantedFeature || !plans) {
      return;
    }

    // The cheapest paid plan this extra is actually sold on.
    const entry = plans
      .filter((candidate) => candidate.price > 0 && wantedFeature.planIds.includes(candidate.id))
      .sort((a, b) => a.price - b.price)[0];

    if (!entry) {
      return;
    }

    hasSeededPlan.current = true;
    // An app already paying keeps its plan: it came for the extra, not for a
    // downgrade to the cheapest tier that happens to sell it.
    setDraftPlanId((current) => current || entry.id);
  }, [wantedFeature, plans]);
  const draftPlan = plans?.find((plan) => plan.id === draftPlanId);
  // A scheduled cancellation is deliberately NOT here. It used to be: every
  // plan went grey and the reader was told to press "Keep this plan" first,
  // which made changing their mind a two-step where buying a plan is the same
  // decision. Paying for a plan now lifts the cancellation as part of the same
  // change — the server does both or neither.
  const blockedReason = isPendingPayment
    ? 'Pay for this app on the Overview before changing its plan.'
    : paymentPastDueAt
    ? 'The last payment failed. Update the card on the Overview before changing the plan.'
    : undefined;

  const onResume = async () => {
    if (!appId) {
      return;
    }

    setIsResuming(true);
    setResumeErrorMessage('');

    try {
      await back4app2.resumeContainerAppPlan(appId);
      onPlanCanceled?.();
    } catch (e) {
      console.error('could not resume the plan', e);
      setResumeErrorMessage(
        e instanceof Error && e.message
          ? e.message
          : "We couldn't keep this plan. Please try again."
      );
    } finally {
      setIsResuming(false);
    }
  };

  const openCancelDialog = async () => {
    setIsConfirmingCancel(true);
    setCancelErrorMessage('');
    setCycleEnd(undefined);

    if (!appId) {
      return;
    }

    try {
      const end = await back4app2.containerBillingCycleEnd(appId);

      if (end) {
        setCycleEnd(new Date(end));
      }
    } catch (e) {
      // The dialog works without the date; it just says less.
      console.error('could not read the billing cycle', e);
    }
  };

  const onConfirmCancel = async () => {
    if (!appId) {
      return;
    }

    setIsCanceling(true);
    setCancelErrorMessage('');

    try {
      await back4app2.cancelContainerAppPlan(appId);
      setIsConfirmingCancel(false);
      setFeatures([]);
      onPlanCanceled?.();
    } catch (e) {
      console.error('could not cancel the plan', e);
      setCancelErrorMessage(
        e instanceof Error && e.message
          ? e.message
          : "We couldn't cancel this plan. Please try again."
      );
    } finally {
      setIsCanceling(false);
    }
  };

  let planRAM = '';
  if (currentPlan?.maxRAM) {
    planRAM = currentPlan.maxRAM >= 1024 ? `${currentPlan.maxRAM / 1024} GB` : `${currentPlan.maxRAM} MB`;
  }

  const formatRAM = (ram?: number) =>
    typeof ram === 'number'
      ? ram >= 1024 ? `${(ram / 1024).toFixed(0)} GB` : `${ram} MB`
      : '';
  const formatTransfer = (gb?: number) =>
    typeof gb === 'number'
      ? gb >= 1000 ? `${(gb / 1000).toFixed(0)} TB` : `${gb} GB`
      : '';

  /**
   * The table lists two "Shared" and three "Dedicated" — the name alone does
   * not identify a row. The RAM is what tells them apart, so it is part of the
   * label rather than a column you have to read across to.
   */
  const planLabel = (plan: Plan) =>
    plan.name === 'Starter' || plan.name === 'Free'
      ? plan.name
      : `${plan.name} ${formatRAM(plan.maxRAM)}`;

  /** What the plan family actually means, in the words that matter. */
  const cpuKind = (plan: Plan) =>
    plan.name.toLowerCase().includes('dedicated') ? 'reserved CPU' : 'shared CPU';

  // The plan that comes with the app does not carry the transfer; the catalog
  // row for the same plan does.
  const currentPlanDetails = plans?.find((plan) => plan.id === currentPlan?.id) || currentPlan;
  const currentSpecs = [
    currentPlanDetails?.maxCPU ? `${currentPlanDetails.maxCPU} vCPU` : undefined,
    formatRAM(currentPlanDetails?.maxRAM) || undefined,
    formatTransfer(currentPlanDetails?.transferLimit)
      ? `${formatTransfer(currentPlanDetails?.transferLimit)} transfer`
      : undefined,
  ].filter(Boolean).join(' · ');

  // Free is not in the table: leaving a paid plan is a cancellation, with its
  // own card at the bottom that says what happens and when.
  const paidPlans = (plans || []).filter((plan) => plan.price > 0);
  const planGroups = [
    { label: 'shared cpu', plans: paidPlans.filter((plan) => cpuKind(plan) === 'shared CPU') },
    { label: 'dedicated cpu', plans: paidPlans.filter((plan) => cpuKind(plan) === 'reserved CPU') },
  ].filter((group) => group.plans.length);
  const activeExtras = (features || []).filter((feature) => feature.status === 'ACTIVE');
  const pendingExtras = (features || []).filter((feature) => feature.status !== 'ACTIVE');
  const extrasLine = [
    ...activeExtras.map((feature) => `${feature.quantity} × ${feature.name}`),
    ...pendingExtras.map((feature) => `${feature.quantity} × ${feature.name} (pending payment)`),
  ].join(', ');

  const selectedPlanId = draftPlanId;

  return <div className="flex flex-col gap-12 lg:gap-14">
    {/* What this app runs on today, what it costs, and how much of its storage
        is in use. One card, one number. */}
    <div className="rounded-container border border-navy-500 bg-navy-800 overflow-hidden">
      {/* About the whole card: every number below only holds until that date. */}
      {isScheduledToCancel ? (
        <div className="border-b border-[#F5B544]/30 bg-[#F5B544]/[0.09] px-6 py-3 lg:px-[26px] flex flex-wrap items-center gap-4">
          <span className="flex-none w-1.5 h-1.5 rounded-full bg-[#F5B544]" />
          <p className="m-0 flex-1 min-w-[240px] text-ds-135 leading-[1.5] text-[#F0DCB4]">
            Moves to the Free plan on {cancelsOnLabel}. You keep {currentPlan?.name}
            {features && features.length ? ' and its extras' : ''} until then.
          </p>
          {/* The way back, next to the news. Nothing is charged: the period is
              already paid for. */}
          <DsButton
            variant="outline"
            size="md"
            className="flex-none !bg-transparent !border-[#F5B544]/45 !text-[#F5C977] hover:!bg-[#F5B544]/15"
            onClick={onResume}
            isDisabled={isResuming || !isOwner}
          >
            {isResuming ? 'Keeping…' : 'Keep this plan'}
          </DsButton>
        </div>
      ) : null}

      {resumeErrorMessage ? (
        <p className="m-0 border-b border-navy-500 px-6 py-3 lg:px-[26px] text-ds-135 leading-[1.5] text-state-err">{resumeErrorMessage}</p>
      ) : null}

      <div className="px-6 py-6 lg:px-[26px] flex flex-wrap items-start gap-6">
        <div className="flex-1 min-w-[260px] flex flex-col gap-2.5">
          <Eyebrow>running now</Eyebrow>
          <div className="min-w-0 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-sora text-[26px] leading-[1.15] font-semibold tracking-[-0.02em] text-white">
              {currentPlan?.name}
            </span>
            <span className="font-courrier text-ds-125 text-navy-200">{currentSpecs}</span>
            {isScheduledToCancel ? (
              <span className="flex-none rounded-control border border-[#F5B544]/45 px-2 py-0.5 font-courrier text-ds-105 uppercase tracking-[0.1em] text-[#F5C977]">
                ends {cancelsOnShort}
              </span>
            ) : null}
          </div>
          <p className="m-0 text-ds-135 leading-[1.5] text-navy-300">
            {extrasLine ? `Plus ${extrasLine}` : 'No extras'}
          </p>
        </div>

        <div className="flex-none flex flex-col items-end gap-0.5">
          <span className="whitespace-nowrap">
            <span className="font-sora text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-white">
              ${monthlyTotal}
            </span>
            <span className="text-ds-135 text-navy-200">
              {' / month'}
              {isScheduledToCancel ? ` until ${cancelsOnShort}` : ''}
            </span>
          </span>
        </div>
      </div>

      {/* How much of the database packs is in use — nothing for an app that
          bought none. */}
      {usage && usage.packs > 0 ? <StorageUsageStrip usage={usage} /> : null}
    </div>

    {!isOwner ? (
      <div className="rounded-container border border-dashed border-navy-500 px-6 py-10 flex flex-col items-center gap-1.5 text-center">
        <span className="text-ds-15 font-semibold text-navy-100">Only the app owner can change the plan</span>
        <span className="text-ds-135 text-navy-300">Ask them to resize the app if it needs more CPU, RAM or transfer.</span>
      </div>
    ) : <>
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        {!isLegacyBilling ? <Eyebrow>step 1 of 2</Eyebrow> : null}
        <h2 className="m-0 font-sora text-[18px] font-semibold tracking-[-0.01em] leading-[1.25] text-white">
          Plan size
        </h2>
        <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">
          {isLegacyBilling
            ? 'Select the plan this app should run on.'
            : 'Select the plan this app should run on. Databases are added in the next step.'}
        </p>
        {/* Why every button below is off, in the one place the eye goes first. */}
        {!isLegacyBilling && blockedReason ? (
          <p className="m-0 text-ds-135 leading-[1.5] text-[#F5C977]">{blockedReason}</p>
        ) : null}
      </div>

      {isLoadingPlans ? (
        <div className="flex justify-center items-center py-10"><LoadingSpinner color='text-white' /></div>
      ) : loadingPlansErrorMessage ? (
        <ErrorMessage message={loadingPlansErrorMessage} />
      ) : (
        <div className="rounded-container border border-navy-500 bg-navy-800 overflow-x-auto b4-thin">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="border-b border-navy-700">
                <th className="pl-[22px] pr-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal w-[30%]">Plan</th>
                <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">CPU</th>
                <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">RAM</th>
                <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">Transfer</th>
                <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">Price</th>
                <th className="pr-[22px] pl-3 py-[13px] w-[140px]"></th>
              </tr>
            </thead>
            {planGroups.map((group, groupIndex) => (
              <tbody key={group.label} className={groupIndex > 0 ? 'border-t border-navy-700' : undefined}>
                <tr>
                  <td colSpan={6} className={`px-[22px] ${groupIndex > 0 ? 'pt-[22px]' : 'pt-3.5'} pb-2`}>
                    <span className="b4-eyebrow !text-ds-105">{group.label}</span>
                  </td>
                </tr>
                {group.plans.map((plan) => {
                  const isCurrent = plan.id === currentPlan?.id;
                  // The picked plan is the marked one — the current plan until
                  // another is picked. The legacy checkout picks nothing here.
                  const isSelected = isLegacyBilling ? isCurrent : plan.id === selectedPlanId;
                  const canPick = !isLegacyBilling && !blockedReason && !isSelected;

                  return (
                    <tr
                      key={plan.id}
                      onClick={canPick ? () => setDraftPlanId(plan.id) : undefined}
                      className={[
                        'transition-colors duration-[120ms]',
                        isSelected ? 'bg-state-ok/[0.09]' : canPick ? 'cursor-pointer hover:bg-navy-700/50' : '',
                      ].join(' ')}
                    >
                      <td className="pl-[22px] pr-3 py-[15px] text-[length:calc(15px*var(--b4-type,1))] font-medium text-white whitespace-nowrap">{planLabel(plan)}</td>
                      <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100">{plan.maxCPU}</td>
                      <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100 whitespace-nowrap">{formatRAM(plan.maxRAM)}</td>
                      <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100 whitespace-nowrap">{formatTransfer(plan.transferLimit)}</td>
                      <td className="px-3 py-[15px] whitespace-nowrap">
                        <span className="text-[16px] font-semibold text-white">${plan.price}</span>
                        <span className="text-ds-125 text-navy-200"> / mo</span>
                      </td>
                      <td className="pr-[22px] pl-3 py-3 text-right">
                        <DsButton
                          variant={isSelected ? 'primary' : 'subtle'}
                          size="lg"
                          className={`min-w-[104px] ${isSelected ? 'pointer-events-none' : ''}`}
                          isDisabled={!isSelected && (isLegacyBilling || Boolean(blockedReason))}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!isLegacyBilling) {
                              setDraftPlanId(plan.id);
                            }
                          }}
                        >
                          {isCurrent ? 'Current' : isSelected ? 'Selected' : 'Select'}
                        </DsButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>

    {appId && !isLegacyBilling && !isLoadingPlans && !loadingPlansErrorMessage ? (
      <PlanPacksEditor
        appId={appId}
        user={user}
        mode={billingMode}
        replacesPreviousPlan={replacesPreviousPlan}
        currentPlan={currentPlan}
        draftPlan={draftPlan && draftPlan.price > 0 ? draftPlan : undefined}
        owned={features || []}
        wantFeatureId={wantedFeature?.id}
        blockedReason={blockedReason}
        liftsCancellation={isScheduledToCancel}
        // Paying ends on the Overview, not back on this table: the change
        // starts a deploy, and the Overview is the only screen that shows it
        // running. A reload here left the buyer on a plan picker with nothing
        // to pick.
        onChanged={() => navigate(`/apps/${appId}`)}
        onDiscard={() => setDraftPlanId(isPaid ? currentPlan?.id : undefined)}
      />
    ) : null}
    </>}

    {/* At the foot of the page, and quiet. Beside the price it sat in the one
        place every visit to this screen looks first, which made leaving the
        most prominent thing on a page that exists to change plans. It is still
        one click, for the people who scroll here looking for it.

        Only an owner, only a paid plan — there is nothing to cancel on Free —
        and never while a cancellation is already scheduled: the way back from
        that lives in the notice at the top. */}
    {isOwner && isPaid && !isScheduledToCancel ? (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pt-5 mt-2 border-t border-navy-700 text-ds-125 text-navy-300">
        <span>You keep the plan until the period you paid for ends.</span>
        <button
          type="button"
          onClick={openCancelDialog}
          className="b4-focus rounded-control underline underline-offset-[3px] hover:text-state-err transition-colors duration-[120ms]"
        >
          Cancel plan
        </button>
      </div>
    ) : null}

    <ModalDialog
      open={isConfirmingCancel}
      onClose={() => setIsConfirmingCancel(false)}
      width="34rem"
      title={<>
        <span className="block mb-2 b4-eyebrow !text-state-err">cancel plan</span>
        {`Cancel the ${currentPlan?.name} plan?`}
      </>}
      blurb={
        <>
          This app is billed {`$${monthlyTotal}`} / month. Cancelling stops the
          billing after the current cycle.
        </>
      }
      footer={
        <>
          <DsButton variant="outline" onClick={() => setIsConfirmingCancel(false)} isDisabled={isCanceling}>
            Keep my plan
          </DsButton>
          <DsButton
            variant="primary"
            className="!bg-state-err hover:!brightness-110 text-white"
            onClick={onConfirmCancel}
            isDisabled={isCanceling}
          >
            {isCanceling ? 'Cancelling…' : 'Cancel plan'}
          </DsButton>
        </>
      }
    >
      {/* Every line here is something that is actually true of OUR
          cancellation. A dialog about money is the worst possible place to
          describe a behaviour the product does not have. */}
      <ul className="list-none m-0 p-0 flex flex-col gap-3">
        {[
          `You keep the ${currentPlan?.name} plan${features && features.length ? ' and its extras' : ''} until the end of the cycle you already paid for. Nothing changes today.`,
          'After that the app moves to the Free plan and keeps running, with the Free plan\'s limits — it sleeps after a while without requests and wakes on the next one.',
          ...(activeExtras.some((feature) => feature.engine)
            ? ['The PostgreSQL database stops being billed on that date. Its data is kept for 7 days, then deleted — export a dump first if you need it.']
            : features && features.length
            ? [`Your ${features.length === 1 ? 'extra stops' : 'extras stop'} being billed on that date.`]
            : []),
          'Your domains, code, environment variables and settings are kept. Pick a paid plan again any time to bring the app back to it.',
          'No refund for the cycle you are in — billing simply stops from the next one.',
        ].map((line, i) => (
          <li key={i} className="flex items-start gap-2.5 text-ds-135 leading-[1.5] text-navy-100">
            <span className="flex-none mt-[7px] w-[5px] h-[5px] rounded-full bg-state-err" />
            <span className="min-w-0">{line}</span>
          </li>
        ))}
      </ul>

      {/* The date the whole dialog is about. Dropped rather than invented when
          Paddle has no cycle to report. */}
      {cycleEnd ? (
        <div className="mt-5 rounded-card border border-navy-500 px-4 py-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <span className="font-courrier text-ds-115 uppercase tracking-[0.1em] text-navy-300">billed until</span>
          <span className="font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-50">
            {cycleEnd.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      ) : null}

      {cancelErrorMessage ? (
        <p className="m-0 mt-4 text-ds-135 leading-[1.5] text-state-err">{cancelErrorMessage}</p>
      ) : null}
    </ModalDialog>
  </div>
}

const BuildAndDeployForm = (props: {sourceKind?: string, imageRepository?: string, imageTag?: string, branchName: string, rootDir: string, repositoryId: string, autoDeploy: boolean, exposedPort: string, startCommand?: string, builderPreference?: string, runtimeVersion?: string, isStaticSite?: boolean, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, plan?: Plan, isFreePlan: boolean, canWrite: boolean, canRead: boolean }) => {
  const { sourceKind, imageRepository, imageTag, branchName, rootDir, repositoryId, autoDeploy, exposedPort, startCommand, builderPreference, runtimeVersion, isStaticSite, isSubmitting, handleSubmit, isFreePlan, canWrite, canRead } = props;

  // An app running a published image is not built here and has no repository:
  // there is no Dockerfile to choose over buildpacks, no branch, no root
  // directory, and no push to auto-deploy from. Showing those controls asked
  // the user to configure a build that never runs — and Branch sat there empty
  // and required-looking, as if the app were misconfigured.
  const isImageSource = sourceKind === 'DOCKER_HUB';
  const [isBrowsingRepository, setIsBrowsingRepository] = useState(false);
  // Runs against the app's SAVED branch and folder, not the ones being edited
  // in this form: the status line describes the app as it stands, and keying it
  // to the inputs would fire a GitHub read on every keystroke.
  const [buildInfo, setBuildInfo] = useState<RepositoryBuildInfo | undefined>();

  useEffect(() => {
    if (!repositoryId || !branchName) {
      return;
    }

    let cancelled = false;

    back4app2
      .repositoryBuildInfo(repositoryId, branchName, rootDir || './')
      .then((result) => {
        if (!cancelled) {
          setBuildInfo(result);
        }
      })
      // A detection that fails costs the status line and nothing else — the
      // field still saves. Settings must not break because GitHub is slow.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [repositoryId, branchName, rootDir]);

  const [formFields, setFormFields] = useState(() => (
    {
      branchName: {
        value: branchName || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      rootDir: {
        value: rootDir || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      exposedPort: {
        value: exposedPort || '',
        isValid: true,
        isRequired: false,
        errorMsg: ''
      },
      startCommand: { value: startCommand || '' },
      runtimeVersion: { value: runtimeVersion || '' },
      // Apps created before the Build Method existed have no stored value:
      // they were required to have a Dockerfile, so NULL reads as DOCKERFILE.
      buildMethod: { value: (builderPreference === 'BUILDPACK' ? 'BUILDPACK' : 'DOCKERFILE') as BuildMethod },
      autoDeploy: !!autoDeploy
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.BRANCH_NAME) {
      if (fieldValue === '') {
        newState.branchName.isValid = false;
        newState.branchName.errorMsg = 'Repository branch name is required!'
      } else {
        newState.branchName.errorMsg = '';
        newState.branchName.isValid = true;
      }
      newState.branchName.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.ROOT_DIR) {
      if (!fieldValue.startsWith('./')) {
        if (fieldValue.startsWith('.') || fieldValue.startsWith('/')) {
          fieldValue = fieldValue.substring(1);
        }
        fieldValue = `./${fieldValue}`;
      }
      newState.rootDir.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.EXPOSED_PORT) {
      if (!fieldValue) {
        newState.exposedPort.value = fieldValue;
        return setFormFields({...newState});
      }
      let val = +fieldValue;
      newState.exposedPort.value = fieldValue;
      newState.exposedPort.errorMsg = '';
      newState.exposedPort.isValid = true;
      if ((isNaN(val) || !(Number.isInteger(val) && val > 0 && val <= 65535)) && val !== undefined) {
        newState.exposedPort.errorMsg = 'Please provide a valid TCP port number (1-65535).'
        newState.exposedPort.isValid = false;
        newState.exposedPort.value = ''
      }
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      branchName: formFields.branchName.value,
      rootDir: formFields.rootDir.value,
      autoDeploy: formFields.autoDeploy,
      exposedPort: +formFields.exposedPort.value,
      startCommand: formFields.startCommand.value,
      builderPreference: formFields.buildMethod.value,
      runtimeVersion: formFields.runtimeVersion.value
    }

    // The API rejects anything that is not a version, and the field already
    // says so — don't turn a typo into a failed save.
    if (newValues.runtimeVersion && !isValidRuntimePin(newValues.runtimeVersion)) {
      return;
    }

    handleSubmit(newValues);
  }

  const initialFields = {
    branchName: branchName || '',
    rootDir: rootDir || '',
    exposedPort: exposedPort || '',
    startCommand: startCommand || '',
    runtimeVersion: runtimeVersion || '',
    buildMethod: (builderPreference === 'BUILDPACK' ? 'BUILDPACK' : 'DOCKERFILE') as BuildMethod,
    autoDeploy: !!autoDeploy,
  };

  const dirtyCount = [
    formFields.branchName.value !== initialFields.branchName,
    formFields.rootDir.value !== initialFields.rootDir,
    formFields.exposedPort.value !== initialFields.exposedPort,
    (formFields.startCommand.value || '') !== initialFields.startCommand,
    (formFields.runtimeVersion.value || '') !== initialFields.runtimeVersion,
    formFields.buildMethod.value !== initialFields.buildMethod,
    formFields.autoDeploy !== initialFields.autoDeploy,
  ].filter(Boolean).length;

  useSettingsSave({
    dirtyCount,
    isSubmitting,
    canSave: canWrite
      && formFields.branchName.isValid
      && formFields.rootDir.isValid
      && formFields.exposedPort.isValid
      && (!formFields.runtimeVersion.value || isValidRuntimePin(formFields.runtimeVersion.value)),
    onSave: () => {
      if (!canWrite && canRead) {
        toast.error('You don\'t have permission to save build and deploy settings.', {
          className: TOAST_SURFACE,
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 4000
        });
        return;
      }
      onSubmit();
    },
    onDiscard: () => setFormFields({
      branchName: { value: initialFields.branchName, isValid: true, isRequired: true, errorMsg: '' },
      rootDir: { value: initialFields.rootDir, isValid: true, isRequired: true, errorMsg: '' },
      exposedPort: { value: initialFields.exposedPort, isValid: true, isRequired: false, errorMsg: '' },
      startCommand: { value: initialFields.startCommand },
      runtimeVersion: { value: initialFields.runtimeVersion },
      buildMethod: { value: initialFields.buildMethod },
      autoDeploy: initialFields.autoDeploy,
    }),
  });

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <div className="flex flex-col">
    {/* Browsing commits only on "Use this folder", so opening it here never
        edits the app by itself — the same contract as the create form. The
        branch comes from the field as it stands, not from what was saved, so
        the listing follows a branch the user just typed. */}
    {repositoryId && (
      <RepositoryBrowser
        open={isBrowsingRepository}
        repositoryId={repositoryId}
        branchName={formFields.branchName.value}
        initialPath={formFields.rootDir.value || ROOT_PATH}
        onClose={() => setIsBrowsingRepository(false)}
        onSelect={(path) => {
          handleFormFields(MyAppSettingsFieldName.ROOT_DIR, path);
          setIsBrowsingRepository(false);
        }}
      />
    )}
    {/* Unlike the create form, Dockerfile is NOT disabled when the repository
        has none: the user may be about to commit one, and the Build Method is
        explicit app configuration — the screen doesn't decide for them. */}
    <SettingsSection eyebrow="source">
      <div className="flex flex-col gap-6">
    {isImageSource ? (
      // Read-only on purpose: this screen saves settings, and changing the
      // image an app runs is a deploy, not a setting. Naming it here still
      // answers the question the tab now raises — "built from what?" — which
      // an empty Build & Deploy screen would leave hanging.
      <div className="space-y-2">
        <div className="font-medium leading-140">Image</div>
        <div className="text-sm opacity-70 truncate">
          {imageRepository ? `${imageRepository}:${imageTag || 'latest'}` : 'Docker Hub'}
        </div>
      </div>
    ) : (
      <div className="space-y-2">
        {/* Same shape as every other label on this screen. TextInput renders
            its label `font-medium leading-140` at the base size; this one was a
            plain 14px div, so the one control without a TextInput was also the
            one whose label looked like body text. */}
        <div className="font-medium leading-140">Build Method</div>
        <BuildMethodSelector
          value={formFields.buildMethod.value}
          onChange={(value) => setFormFields(prev => ({ ...prev, buildMethod: { value } }))}
          isDisabled={isSubmitting || !canWrite}
          groupName="settingsBuildMethod"
        />
      </div>
    )}
    {/* Its own field, not the tail of the Build Method block: nested in there
        it inherited the 8px label-to-control gap and sat glued under the
        radio cards, reading as part of them rather than as the next question.
        Only on the buildpack path — a Dockerfile names its own runtime in its
        FROM line. */}
    {formFields.buildMethod.value === 'BUILDPACK' && (
      <RuntimeVersionField
        detectedStack={buildInfo?.detectedStack}
        resolved={buildInfo?.runtimeVersion}
        value={formFields.runtimeVersion.value}
        onChange={(value) => setFormFields(prev => ({ ...prev, runtimeVersion: { value } }))}
        disabled={isSubmitting || !canWrite}
      />
    )}
    {!isImageSource && <TextInput
      value={formFields.branchName.value}
      label="Branch"
      name="branch" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.BRANCH_NAME, e.target.value.trim())} 
      placeholderText="Branch"
      infoText="The repository branch that your web application uses."
      isError={!formFields.branchName.isValid}
      errorMessage={formFields.branchName.errorMsg}
      isDisabled={isSubmitting || !canWrite}
    />}
    {!isImageSource && <TextInput 
      value={formFields.rootDir.value} 
      label="Root directory" 
      name="rootDirPath" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.ROOT_DIR, e.target.value.trim())} 
      placeholderText="./"
      infoText="By default, the root directory is the repository's root. If you specify a different directory, Back4app runs all commands within that directory only and ignores any changes outside it."
      isError={!formFields.rootDir.isValid}
      errorMessage={formFields.rootDir.errorMsg}
      isDisabled={isSubmitting || !canWrite}
      // Never disabled while the user can write: "no subfolders" is only
      // knowable after a listing, and an empty listing is also what a mistyped
      // path produces — exactly when the browser is the way out. The modal
      // answers in words; a dead control explains nothing.
      endAdornment={
        repositoryId && canWrite ? (
          <button
            type="button"
            className="b4-focus inline-flex text-action-soft hover:text-white text-ds-125 font-semibold items-center whitespace-nowrap"
            onClick={() => setIsBrowsingRepository(true)}
          >
            <FolderIcon className="mr-[0.625rem]" /> Browse folders
          </button>
        ) : undefined
      }
    />}
      </div>
    </SettingsSection>

    {!isImageSource ? (
      <SettingsSection eyebrow="deploy">
      <ToggleSwitchCheckbox 
        value={formFields.autoDeploy} 
        onChange={() => setFormFields(prev => ({...prev, autoDeploy: !prev.autoDeploy}))} 
        labelText="Autodeploy" 
        description="Deploy on every repository push. Off means you trigger every deploy yourself."
        disabled={isFreePlan || !canWrite}
        infoText={isFreePlan ? "This feature is available on paid plans. Upgrade your plan to automatically deploy your application whenever changes are pushed to your repository.": null}
      />
      </SettingsSection>
    ) : null}

    <SettingsSection eyebrow="runtime">
      <div className="flex flex-col gap-6">
    <TextInput 
      value={`${formFields.exposedPort.value}`} 
      label="Port" 
      name="exposedPort" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.EXPOSED_PORT, e.target.value.trim())} 
      placeholderText="8080"
      infoText="The port your app listens on. Leave it empty and Back4app detects it automatically."
      isError={!formFields.exposedPort.isValid}
      errorMessage={formFields.exposedPort.errorMsg}
      isDisabled={!canWrite}
    />
    {/* The whole block — label, input and the static-site line — belongs to the
        buildpack route and is hidden on Dockerfile, matching the create form.
        Nothing here is read by the kaniko path: `start_command` is consumed
        only by the CNB shim, and `is_static_site` only by the CNB entrypoint,
        so on Dockerfile these controls describe something that cannot happen.

        Hiding does NOT lose the saved command. `formFields` lives in this
        component, not inside TextInput, so unrendering the input leaves
        `formFields.startCommand.value` untouched; onSubmit still sends the
        value it read from the app, handleSubmit finds no difference, and
        nothing is written. Switching back to Buildpacks shows it again. */}
    {formFields.buildMethod.value === 'BUILDPACK' && (
      <>
        <TextInput
          value={formFields.startCommand.value}
          label="Start Command"
          name="startCommand"
          onChange={(e) => setFormFields(prev => ({ ...prev, startCommand: { value: e.target.value } }))}
          placeholderText="e.g. gunicorn app:app"
          infoText="Optional. Leave empty and we'll use your Procfile or start script. Set it when your app has neither. The app must listen on the PORT env var (or set the Port above)."
          isError={false}
          errorMessage=""
          isDisabled={!canWrite}
        />
        {/* Static serving is detected once, when the app is created. A
            repository that later grows a server would keep serving its build
            output with the server never running — so the way out has to be
            visible, and it is the Start Command right above rather than one
            more switch to explain.

            Gone once a command is set, because by then the sentence is false:
            `cnbBuild` reads `is_static_site` only for an empty command, so the
            stored flag is dormant and the app is served by the command above.
            It comes back if the field is cleared, which is exactly what it
            promises. */}
        {isStaticSite && !formFields.startCommand.value && (
          <div className="text-sm opacity-70 -mt-4">
            This app is served as a static site. Set a Start Command to run a server instead.
          </div>
        )}
      </>
    )}
      </div>
    </SettingsSection>


  </div>
}

const HealthAndAlertForm = (props: {healthCheckEndpoint: string, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, canWrite: boolean, canRead: boolean }) => {
  const { healthCheckEndpoint, isSubmitting, handleSubmit, canWrite, canRead } = props;
  const [formFields, setFormFields] = useState(() => (
    {
      healthCheckEndpoint: {
        value: healthCheckEndpoint || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      }
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT) {
      if (fieldValue[0] !== '/') {
        fieldValue = `/${fieldValue}`
      }
      newState.healthCheckEndpoint.value = fieldValue;
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      healthCheckEndpoint: formFields.healthCheckEndpoint.value,
    }
    handleSubmit(newValues);
  }

  const isDirty = formFields.healthCheckEndpoint.value !== (healthCheckEndpoint || '');

  useSettingsSave({
    dirtyCount: isDirty ? 1 : 0,
    isSubmitting,
    canSave: canWrite && formFields.healthCheckEndpoint.isValid,
    onSave: () => {
      if (!canWrite && canRead) {
        toast.error('You don\'t have permission to save health check settings.', {
          className: TOAST_SURFACE,
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 4000
        });
        return;
      }
      onSubmit();
    },
    onDiscard: () => setFormFields({
      healthCheckEndpoint: { value: healthCheckEndpoint || '', isValid: true, isRequired: true, errorMsg: '' }
    }),
  });

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <div className="flex flex-col">
    <SettingsSection eyebrow="health check">
      <div className="max-w-[420px]">
        <TextInput
          value={formFields.healthCheckEndpoint.value}
          label="Health check path"
          name="healthCheckEndpoint"
          onChange={(e) => handleFormFields(MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT, e.target.value.trim())}
          placeholderText="/checkpath"
          infoText="An HTTP request performed during deployments to confirm that a service is healthy."
          isError={!formFields.healthCheckEndpoint.isValid}
          errorMessage={formFields.healthCheckEndpoint.errorMsg}
          isDisabled={isSubmitting || !canWrite}
        />
      </div>
    </SettingsSection>


  </div>
}

const VariablesForm = (props: { appId: string, envVars: { id: string, key: string; value?: string }[], databaseUrlEnvKey: string, hasDatabaseUrlOverride: boolean, isSubmitting: boolean, handleSubmit: (newValues: AppForm) => Promise<boolean>, canWrite: boolean, canRead: boolean }) => {
  const { appId, envVars, databaseUrlEnvKey, hasDatabaseUrlOverride, isSubmitting, handleSubmit, canWrite, canRead } = props;

  // Whether the database extra is active, which is what puts the managed row
  // here. Its own read: the variables are the subject of this tab, and a failed
  // extras read only means the row is not offered.
  const [hasDatabase, setHasDatabase] = useState(false);

  useEffect(() => {
    let canceled = false;

    back4app2.containerAppFeatures(appId)
      .then((rows) => {
        if (!canceled) {
          setHasDatabase(rows.some((row) => row.status === 'ACTIVE' && !!row.engine));
        }
      })
      .catch((e) => console.error('could not load the app extras', e));

    return () => { canceled = true; };
  }, [appId]);

  // The database variable: its name, and optionally a connection string of the
  // app's own in place of Back4app's. It cannot be removed. Neither value is
  // ever read back — the managed one never reaches the API, and the app's own
  // is write-only — so the value field only ever holds what is being typed.
  // Every rule on both is the API's; this form sends and shows its answer.
  const [databaseKey, setDatabaseKey] = useState(databaseUrlEnvKey);
  const [databaseValue, setDatabaseValue] = useState('');
  const [showDatabaseValue, setShowDatabaseValue] = useState(false);
  const [isEditingDatabaseKey, setIsEditingDatabaseKey] = useState(false);

  useEffect(() => { setDatabaseKey(databaseUrlEnvKey); }, [databaseUrlEnvKey]);
  const initialFields = useMemo(() => envVars.map(env => ({
    id: Math.random().toString(16).slice(2),
    name: { isValid: true, value: env.key, errorMsg: '' },
    value: { isValid: true, value: env.value, showValue: false, errorMsg: '' }
  })), [envVars]);
  const [formFields, setFormFields] = useState(initialFields);
  /** The one row currently open for editing; the rest stay as read rows. */
  const [editingId, setEditingId] = useState<string | null>(null);

  // Renames count twice — once as a variable that no longer exists, once as one
  // that did not. That is what happened, and the bar only has to say "how many
  // things did I touch", not reconstruct the diff.
  const dirtyCount = useMemo(() => {
    const before = new Map(envVars.map(env => [env.key, env.value ?? '']));
    const after = new Map(formFields.map(env => [env.name.value, env.value.value ?? '']));
    let count = 0;
    after.forEach((value, key) => {
      if (!before.has(key) || before.get(key) !== value) {
        count += 1;
      }
    });
    before.forEach((_value, key) => {
      if (!after.has(key)) {
        count += 1;
      }
    });
    if (databaseKey !== databaseUrlEnvKey) {
      count += 1;
    }
    if (databaseValue) {
      count += 1;
    }
    return count;
  }, [envVars, formFields, databaseKey, databaseUrlEnvKey, databaseValue]);

  const validateEnvVariable = (name: string, value = '') => {
    const isCombinedLengthValid = name.length + value.length <= ENV_VARIABLE_MAX_LENGTH;
    const isValid = !!name.match(/(^[A-Z_])[A-Z0-9_]+$/);
    return {
      name: {
        isValid: isValid && isCombinedLengthValid,
        errorMsg: isValid ? '' : 'Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers'
      },
      value: {
        isValid: isCombinedLengthValid,
        errorMsg: isCombinedLengthValid ? '' : `The combined length of the variable name and value must not exceed ${ENV_VARIABLE_MAX_LENGTH} characters.`
      }
    };
  };

  const handleOnChangeEnvVars = (id: string, newValue: string, type: 'name' | 'value') => {
    const newEnvVariableList = formFields.map(env => {
      if (id === env.id && type === 'name') {
        const validation = validateEnvVariable(newValue, env.value.value);
        return {
          ...env,
          name: { value: newValue, ...validation.name },
          value: { ...env.value, ...validation.value }
        }
      } else if (id === env.id && type === 'value') {
        const validation = validateEnvVariable(env.name.value, newValue);
        return {
          ...env,
          name: { ...env.name, ...validation.name },
          value: { ...env.value ,value: newValue, ...validation.value }
        }
      }
      return env;
    });
    setFormFields([...newEnvVariableList]);
  }

  const handleRemoveEnviromentVar = (id: string) => {
    const newEnvVariableList = formFields.filter(env => env.id !== id);
    setFormFields(newEnvVariableList);
  }

  const toggleEnvVariableVisibilty = (id: string) => {
    const newEnvVariableList = formFields.map(env => {
      if (env.id === id) {
        return { ...env, value: {...env.value, showValue: !env.value.showValue} }
      }
      return env;
    });
    setFormFields(newEnvVariableList);
  }

  const onSubmit = async (overrides: Partial<AppForm> = {}) => {
    const validatedFields = formFields.map(env => {
      const validation = validateEnvVariable(env.name.value, env.value.value);
      return {
        ...env,
        name: { ...env.name, ...validation.name },
        value: { ...env.value, ...validation.value }
      };
    });
    const inValidEnv = validatedFields.findIndex(env => !env.name.isValid || !env.value.isValid);
    if (formFields.length && inValidEnv !== -1) {
      setFormFields(validatedFields);
      return false;
    }
    const envs = formFields.map(env => ({
      key: env.name.value,
      value: env.value.value
    }));
    const saved = await handleSubmit({
      envs,
      databaseUrlEnvKey: hasDatabase && databaseKey !== databaseUrlEnvKey ? databaseKey : undefined,
      databaseUrlValue: hasDatabase && databaseValue ? databaseValue : undefined,
      ...overrides,
    });
    if (saved) {
      setDatabaseValue('');
      setShowDatabaseValue(false);
      setIsEditingDatabaseKey(false);
    }
    return saved;
  }

  const denied = (what: string) => {
    toast.error(`You don't have permission to ${what} environment variables.`, {
      className: TOAST_SURFACE,
      icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
      duration: 4000
    });
  };

  useSettingsSave({
    dirtyCount,
    isSubmitting,
    canSave: canWrite && formFields.every(env => env.name.isValid && env.value.isValid),
    onSave: () => canWrite ? onSubmit() : denied('save'),
    onDiscard: () => { setEditingId(null); setFormFields(initialFields); setDatabaseKey(databaseUrlEnvKey); setDatabaseValue(''); setShowDatabaseValue(false); setIsEditingDatabaseKey(false); },
  });

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  const startAdding = () => {
    if (!canWrite) {
      denied('add');
      return;
    }

    // An unnamed row is the row you already asked for. Pressing Add again used
    // to stack another blank one on top of it, and none of them could be
    // saved — the API rejects an empty key — so the button quietly built a
    // list of rows the form would refuse. It reopens that row and says why
    // instead.
    const blank = formFields.find(env => !env.name.value);
    if (blank) {
      setFormFields(prev => prev.map(env => env.id === blank.id
        ? { ...env, name: { ...env.name, isValid: false, errorMsg: 'Give this one a name first.' } }
        : env));
      setEditingId(blank.id);
      return;
    }

    const id = Math.random().toString(16).slice(2);
    setFormFields(prev => [...prev, {
      id,
      name: { isValid: true, value: '', errorMsg: '' },
      value: { isValid: true, value: '', showValue: true, errorMsg: '' },
    }]);
    setEditingId(id);
  };

  return <div className="flex flex-col gap-4">
    {/* The head sits on the page, not inside the box: it names the table and
        acts on it, and the card's edge belongs to the rows. An eyebrow rather
        than a heading — the page title above already says "Environment
        variables", and every other tab names its groups this way. */}
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-baseline gap-2">
        <Eyebrow>variables</Eyebrow>
        <span className="font-courrier text-ds-115 text-navy-300">{formFields.length}</span>
      </span>
      <DsButton size="md" onClick={startAdding}>
        <PlusIcon className="w-3 h-3" />Add variable
      </DsButton>
    </div>

    <SurfaceCard tone="surface" radius="container">
      {hasDatabase ? (
        isEditingDatabaseKey ? (
          <div className="border-b border-navy-500 px-4 py-4 lg:px-5 flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <TextInput
                value={databaseKey}
                label="Name"
                name="env-name-managed-database"
                onChange={(e) => setDatabaseKey(e.target.value.trim())}
                placeholderText={DEFAULT_DATABASE_URL_ENV_KEY}
                isDisabled={!canWrite}
              />
              {/* Empty is not "no value": it keeps whatever the variable
                  carries now. Typing is the only way to put a connection
                  string of the app's own in, and "Use Back4app database" the
                  only way back. */}
              <TextInput
                type={showDatabaseValue ? 'text' : 'password'}
                value={databaseValue}
                label="Value"
                name="env-value-managed-database"
                onChange={(e) => setDatabaseValue(e.target.value)}
                placeholderText={hasDatabaseUrlOverride ? 'Your connection string (hidden)' : 'Set by Back4app'}
                endAdornment={
                  <span className="cursor-pointer" onClick={() => setShowDatabaseValue(!showDatabaseValue)}>
                    {showDatabaseValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </span>
                }
                isDisabled={!canWrite}
              />
            </div>
            <span className="text-ds-125 text-navy-300">
              {hasDatabaseUrlOverride
                ? 'Using your own database. Paste a new connection string to replace it.'
                : 'Paste a connection string to use your own database instead.'}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <DsButton
                variant="primary"
                size="md"
                onClick={() => {
                  if (!canWrite) { denied('save'); return; }
                  onSubmit();
                }}
                isDisabled={isSubmitting || (databaseKey === databaseUrlEnvKey && !databaseValue)}
              >
                <CheckIcon size={13} />{isSubmitting ? 'Saving…' : 'Save'}
              </DsButton>
              <button
                type="button"
                className="b4-focus rounded-control px-2 h-[30px] text-ds-125 font-semibold text-navy-200 hover:bg-navy-700 transition-colors duration-[120ms]"
                onClick={() => { setDatabaseKey(databaseUrlEnvKey); setDatabaseValue(''); setShowDatabaseValue(false); setIsEditingDatabaseKey(false); }}
              >
                Cancel
              </button>
              {hasDatabaseUrlOverride ? (
                <button
                  type="button"
                  className="b4-focus ml-auto rounded-control px-2 h-[30px] text-ds-125 font-semibold text-navy-200 hover:bg-navy-700 transition-colors duration-[120ms]"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (!canWrite) { denied('save'); return; }
                    setDatabaseValue('');
                    onSubmit({ databaseUrlValue: '' });
                  }}
                >
                  Use Back4app database
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="border-b border-navy-500 flex items-center gap-3 px-4 lg:px-5 hover:bg-navy-700/50 transition-colors duration-[120ms]">
            {/* Editing is the only thing to do with this row: the name and the
                value can change, but it cannot be deleted — the app bought a
                database and always receives one. */}
            <button
              type="button"
              className="b4-focus flex-1 min-w-0 flex flex-col gap-1 py-3 text-left sm:flex-row sm:items-center sm:gap-4"
              onClick={() => canWrite ? setIsEditingDatabaseKey(true) : denied('edit')}
            >
              <span className="sm:w-[38%] font-courrier text-ds-135 text-white truncate">{databaseUrlEnvKey}</span>
              <span className="flex-1 min-w-0 text-ds-125 text-navy-300 truncate">
                {hasDatabaseUrlOverride
                  ? 'Your own database · connection string hidden'
                  : 'Set by Back4app · your database connection, never shown'}
              </span>
            </button>
            <span className="flex-none font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300 pr-1.5">edit</span>
          </div>
        )
      ) : null}
      {formFields.length === 0 ? (
        <div className="px-4 py-10 lg:px-5 text-center text-ds-135 text-navy-300">
          No variables yet.
        </div>
      ) : formFields.map((enviromentVar) => {
        const { id, name, value } = enviromentVar;
        const envValue = value.value || '';
        const isEditing = editingId === id;

        if (isEditing) {
          return (
            <div key={id} className="border-t border-navy-500 first:border-t-0 px-4 py-4 lg:px-5 flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput
                  value={name.value}
                  label="Name"
                  name={`env-name-${id}`}
                  onChange={(e) => handleOnChangeEnvVars(id, e.target.value.trim(), 'name')}
                  placeholderText="EXAMPLE_NAME"
                  isError={!name.isValid}
                  errorMessage={name.errorMsg}
                  isDisabled={!canWrite}
                />
                <TextInput
                  type={value.showValue ? 'text' : 'password'}
                  value={envValue}
                  label="Value"
                  name={`env-value-${id}`}
                  onChange={(e) => handleOnChangeEnvVars(id, e.target.value, 'value')}
                  placeholderText="A1B2C3D4E5F6"
                  endAdornment={
                    <span className="cursor-pointer" onClick={() => toggleEnvVariableVisibilty(id)}>
                      {value.showValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </span>
                  }
                  isError={!value.isValid}
                  errorMessage={value.errorMsg}
                  isDisabled={!canWrite}
                />
              </div>
              <div className="flex items-center gap-2">
                {/* This used to be "Done", and it only closed the editor —
                    nothing was written until you found the save bar pinned to
                    the bottom of the window. A row that says it is done and
                    changes nothing is the worst of both, so the button commits
                    the section now and says so. */}
                <DsButton
                  variant="primary"
                  size="md"
                  onClick={() => {
                    if (!canWrite) { denied('save'); return; }
                    setEditingId(null);
                    onSubmit();
                  }}
                  isDisabled={isSubmitting || !name.isValid || !value.isValid || !name.value}
                >
                  <CheckIcon size={13} />{isSubmitting ? 'Saving…' : 'Save'}
                </DsButton>
                <button
                  type="button"
                  className="b4-focus rounded-control px-2 h-[30px] text-ds-125 font-semibold text-state-err hover:bg-state-err/10 transition-colors duration-[120ms]"
                  onClick={() => {
                    if (!canWrite) { denied('delete'); return; }
                    setEditingId(null);
                    handleRemoveEnviromentVar(id);
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        }

        return (
          <div
            key={id}
            className="group border-t border-navy-500 first:border-t-0 flex items-center gap-3 px-4 lg:px-5 hover:bg-navy-700/50 transition-colors duration-[120ms]"
          >
            {/* The row is the edit affordance. A pencil per row on a screen
                that is nothing but rows is five pencils saying the same thing. */}
            <button
              type="button"
              className="b4-focus flex-1 min-w-0 flex flex-col gap-1 py-3 text-left sm:flex-row sm:items-center sm:gap-4"
              onClick={() => canWrite ? setEditingId(id) : denied('edit')}
            >
              <span className="sm:w-[38%] font-courrier text-ds-135 text-white truncate">
                {name.value || <span className="text-navy-300">unnamed</span>}
              </span>
              <span className="flex-1 min-w-0 font-courrier text-ds-135 text-navy-100 truncate">
                {value.showValue ? envValue : '•'.repeat(Math.min(Math.max(envValue.length, 8), 16))}
              </span>
            </button>
            <button
              type="button"
              aria-label={value.showValue ? `Hide the value of ${name.value}` : `Show the value of ${name.value}`}
              className="b4-focus flex-none rounded-control p-1.5 text-navy-300 hover:text-white transition-colors duration-[120ms]"
              onClick={() => toggleEnvVariableVisibilty(id)}
            >
              {value.showValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
            <button
              type="button"
              aria-label={`Delete ${name.value}`}
              className="b4-focus flex-none rounded-control p-1.5 text-navy-300 hover:text-state-err transition-colors duration-[120ms]"
              onClick={() => canWrite ? handleRemoveEnviromentVar(id) : denied('delete')}
            >
              <TrashIcon />
            </button>
          </div>
        );
      })}

      <div className="border-t border-navy-500 px-4 py-3.5 lg:px-5 text-ds-135 text-navy-300">
        Changing a variable takes effect on the next deploy.
      </div>
    </SurfaceCard>


  </div>
};

const SuspendDeleteForm = (props: { appName: string, appId: string, block?: AppBlock, updateIsDeletingApp: (isDeleting: boolean) => void }) => {
  const { appName, appId, block, updateIsDeletingApp } = props;
  const [openModal, setOpenModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAppErrorMessage, setDeleteAppErrorMessage] = useState('');
  const [verifyAppNameInput, setVerifyAppNameInput] = useState(() => ({
    value: '',
    isError: false,
    isValid: true,
    isTouched: false,
    errorMsg: ''
  }));

  const isConfirmed = verifyAppNameInput.value === appName && !!appName;

  const onChangeDeleteAppInput = (val: string) => {
    let isValid=true, errorMsg='';
    if (val !== appName) {
      isValid = false; errorMsg = 'App name entered does not match!';
    }
    setVerifyAppNameInput({
      ...verifyAppNameInput,
      value: val,
      isValid,
      errorMsg,
      isTouched: true
    });
  };

  const handleDeleteApp = async () => {
    if (!isConfirmed) {
      return;
    }
    setIsDeleting(true);
    updateIsDeletingApp(true);
    trackEvent(AmplitudeEvent.CLICK_ON_DELETE_APP);
    try {
      await back4app2.deleteApp(appId as string);
      updateIsDeletingApp(false);
      window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`;
    } catch (err) {
      if (err instanceof NetworkError) {
        setDeleteAppErrorMessage('Network error when deleting app. Please check your internet connection or try again later.');
      } else if (err instanceof AuthenticationError || err instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (err instanceof BlockedResourceError) {
        // Refused on purpose, and permanently until the review ends. "Try
        // again later" would be a lie the customer could act on all day.
        // Reachable even with the button guarded below, when the block lands
        // between this page loading and the click.
        setDeleteAppErrorMessage(err.message);
      } else {
        console.error('error deleting app', err);
        setDeleteAppErrorMessage('Something went wrong, please try again later.');
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return <div className="flex flex-col">
    {/* One row, because Delete is the only thing this app can actually do here
        — suspend and transfer-ownership are not in the API, and a disabled row
        promising them would be a worse answer than their absence. Rules
        separate the rows; the section eyebrow above says "danger zone", so the
        colour is carried by the one title that needs it. */}
    <div className="py-5 first:pt-0 border-b border-navy-700 last:border-b-0 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="flex flex-col gap-1.5 min-w-0 max-w-[60ch]">
        <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-state-err">
          Delete this web app
        </span>
        {/* Said before the ritual, not after it. Letting someone open the modal
            and type their app's name to confirm a deletion we will refuse is a
            worse way to deliver the same no. */}
        <span className="text-ds-135 leading-[1.55] text-navy-200">
          {block
            ? "This app is under security review and can't be deleted while the review is open."
            : 'Every resource stops immediately and cannot be recovered.'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpenModal(true)}
        disabled={!!block}
        className={`b4-focus flex-none inline-flex items-center justify-center h-[38px] px-4 rounded-surface border text-[length:calc(14px*var(--b4-type,1))] font-semibold transition-colors duration-[120ms] ${
          block
            ? 'border-transparent bg-navy-600 text-navy-300 cursor-not-allowed'
            : 'border-state-err/60 bg-transparent text-state-err hover:bg-state-err/10 hover:border-state-err cursor-pointer'
        }`}
      >
        Delete
      </button>
    </div>

    <ModalDialog
      open={openModal}
      onClose={() => setOpenModal(false)}
      icon={<TriangleExclamationIcon className="text-state-err" width="20px" height="20px" />}
      title="Delete web app"
      blurb={<>
        This will permanently delete{' '}
        <span className="font-courrier text-navy-100">{appName}</span>{' '}
        and everything it runs. It cannot be recovered.
      </>}
      footer={<>
        <DsButton onClick={() => setOpenModal(false)} isDisabled={isDeleting}>Cancel</DsButton>
        <DsButton variant="danger" isDisabled={!isConfirmed || isDeleting} onClick={handleDeleteApp}>
          {isDeleting ? 'Deleting…' : 'Delete'}
        </DsButton>
      </>}
    >
      <div className="flex flex-col gap-2">
        {/* The typing stays. An irreversible delete is worth the extra beat. */}
        <TextInput
          value={verifyAppNameInput.value}
          label="Type the app name to confirm"
          name="confirmDeleteAppName"
          onChange={(e) => onChangeDeleteAppInput(e.target.value.trim())}
          placeholderText={appName}
          isError={verifyAppNameInput.isTouched && !verifyAppNameInput.isValid}
          errorMessage={verifyAppNameInput.errorMsg}
          isDisabled={isDeleting}
        />
        {deleteAppErrorMessage ? (
          <span className="text-ds-115 text-state-err">{deleteAppErrorMessage}</span>
        ) : null}
      </div>
    </ModalDialog>
  </div>
}

export default MyAppSettings
